import { z as zod } from 'zod';
import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';

import Box from '@mui/material/Box';
import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';

import { RHFTextField } from 'src/components/hook-form';

import { useReportContext } from './use-report-context';

// ----------------------------------------------------------------------

const CONTEXT_MAX_LENGTH = 1000;

const ReportContextSchema = zod.object({
  context: zod
    .string()
    .max(CONTEXT_MAX_LENGTH, {
      message: `Context must be at most ${CONTEXT_MAX_LENGTH} characters`,
    })
    .default(''),
});

// ----------------------------------------------------------------------

export function ReportContextDialog({ open, onClose, report }) {
  const { getContext, saveContext } = useReportContext();
  const [loading, setLoading] = useState(false);

  const methods = useForm({
    resolver: zodResolver(ReportContextSchema),
    defaultValues: { context: '' },
  });

  const {
    reset,
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const context = watch('context') || '';

  useEffect(() => {
    if (!open || !report?.uri) {
      return;
    }

    let cancelled = false;
    setLoading(true);

    getContext(report.uri)
      .then((value) => {
        if (!cancelled) {
          reset({ context: value || '' });
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Failed to load report context');
          reset({ context: '' });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, report?.uri, getContext, reset]);

  const handleClose = () => {
    reset({ context: '' });
    onClose();
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      await saveContext(report.uri, data.context);
      toast.success('Report context updated');
      onClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || error.message || 'Failed to update report context';
      toast.error(errorMessage);
    }
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit report context</DialogTitle>

      <DialogContent dividers>
        <FormProvider {...methods}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ mt: 3 }}>
              <RHFTextField
                name="context"
                label="Context"
                multiline
                rows={6}
                helperText={`${context.length}/${CONTEXT_MAX_LENGTH} characters`}
                slotProps={{
                  htmlInput: {
                    autoComplete: 'off',
                    maxLength: CONTEXT_MAX_LENGTH,
                  },
                }}
              />
            </Box>
          )}
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={onSubmit}
          loading={isSubmitting}
          disabled={loading}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
